import { useCallback, useEffect, useMemo, useState } from 'react';
import errorHandle from '../../utils/errorHandle';
import ReactLoading from 'react-loading';
import { toPascalCase } from '../../utils/pascalCase';
import { ReactComponent as CloseIcon } from '../../assets/icons/trvto_close.svg';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { dashboardServiceConfig } from '../../utils/supportServiceConfig/dashboardServiceConfig';
import { supBookingsServiceConfig } from '../../utils/supportServiceConfig/supBookingsServiceConfig';
import AddBooking from '../../pages/supTripDateDetails/addBooking/AddBooking';
import AddBookingTrip from './AddBookingTrip';
import AddBookingAct from './AddBookingAct';
import AddBookingGu from './AddBookingGu';
import StayManualBooking from '../../pages/supBooking/bookingTypes/StayManualBooking';

const MODE_TO_SERVICE = {
    t: 'trip',
    a: 'act',
    c: 'camp',
    g: 'gu'
};

export default function AddBookingSup({ setAddBo }) {
    const navigate = useNavigate();
    const { mode } = useParams();
    const [serviceType, setServiceType] = useState(MODE_TO_SERVICE[mode] || 'trip');
    const selectedType = 'upcoming';
    const [selectedOrg, setSelectedOrg] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const selected = searchParams.get('type') || 'next';
    const [datesType, setDatesType] = useState('fixed');
    const [group, setGroup] = useState('backpacking');
    const [trips, setTrips] = useState([]);
    const [load, setLoad] = useState(true);
    const [selectedTrip, setSelectedTrip] = useState('');
    const [tripDates, setTripDates] = useState([]);
    const pageNumber = Number(searchParams.get('page')) || 1;
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [orgData, setOrgData] = useState([]);
    const type = 'trip';
    const limit = 10;
    const [kpiData, setKpiData] = useState([]);
    const offSet = (pageNumber - 1) * limit;
    const [totalCount, setTotalCount] = useState(0);
    const totalData = totalCount;
    const totalPages = Math.ceil(totalData / limit);
    const refresh = () => {
        setLoad(!load);
    };
    const serviceConfig = dashboardServiceConfig[serviceType] || {};
    const serviceBookingConfig = supBookingsServiceConfig[serviceType];

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    const SERVICE_TO_MODE = {
        trip: 't',
        act: 'a',
        camp: 'c',
        guide: 'g'
    };

    const handleServiceType = (e) => {
        const selectedValue = e.target.value;
        setServiceType(selectedValue);
    };

    useEffect(() => {
        if (!serviceConfig?.fetchOrg) return;
        const fetchOrg = async () => {
            serviceConfig.fetchOrg({ serviceType, setOrgData, setLoading });
        };
        fetchOrg();
    }, [serviceType]);

    useEffect(() => {
        if (!selectedOrg || !serviceConfig?.fetchCount) return;

        const fetchCount = async () => {
            try {
                await serviceConfig.fetchCount({
                    selectedOrg,
                    selected,
                    setTotalCount,
                    setLoading,
                    serviceType
                });
            } catch (error) {
                errorHandle(error);
            }
        };

        fetchCount();
    }, [selected, selectedOrg, serviceType]);

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    useEffect(() => {
        // only trips have dates to pick here
        if (!selectedTrip || !selectedOrg || !serviceBookingConfig?.fetchTripDates) return;
        serviceBookingConfig.fetchTripDates({ selectedTrip, setTripDates, selectedType, setLoading });
    }, [selectedType, selectedTrip, selectedOrg, datesType]);

    useEffect(() => {
        if (!selectedOrg || !serviceBookingConfig?.fetchTrips) {
            setTrips([]);
            return;
        }

        async function fetchTripsData() {
            try {
                await serviceBookingConfig.fetchTrips({
                    selectedOrg,
                    setTrips,
                    datesType,
                    group,
                    setLoading,
                    serviceType
                });
            } catch (error) {
                setLoading(false);
                errorHandle(error);
            }
        }

        fetchTripsData();
    }, [selectedOrg, datesType, serviceType, group]);

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalData / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };
    const packData = useMemo(() => trips.find((data) => data._id === selectedTrip), [trips, selectedTrip]);

    return (
        <div className='orgAddBoBack'>
            <div className='orgAddBoDiv'>
                <h3 className='orgAddBoH1'>Add New Booking</h3>
                <button onClick={() => setAddBo(false)} className='orgAddBoCancel'>
                    <CloseIcon className='orgAddBoCloseIcon' />
                </button>
                <div className='orgAddBoSubDiv'>
                    <select
                        name='serviceType'
                        id='serviceType'
                        className='select mr-2'
                        value={serviceType}
                        onChange={(e) => {
                            handleServiceType(e);
                            setSelectedOrg('');
                            setSelectedTrip('');
                        }}
                    >
                        <option value='trip'>Trip</option>
                        <option value='act'>Activity</option>
                        <option value='camp'>Camp</option>
                        <option value='gu'>Guide</option>
                    </select>
                    <select
                        name='org'
                        id='org'
                        className='select mr-2'
                        value={selectedOrg}
                        onChange={(e) => {
                            const selectedId = e.target.value;
                            setSelectedOrg(selectedId);
                            setSelectedTrip('');
                        }}
                    >
                        <option value=''>Select Organizer</option>
                        {orgData.map((org) => (
                            <option value={org._id} key={org._id}>
                                {org.companyName}
                            </option>
                        ))}
                    </select>
                    {serviceType === 'trip' && (
                        <>
                            <select
                                name='datesType'
                                id='datesType'
                                className='select'
                                value={datesType}
                                onChange={(e) => {
                                    const selectedType = e.target.value;
                                    setDatesType(selectedType);
                                }}
                            >
                                <option value='fixed'>Fixed</option>
                                <option value='custom'>Custom</option>
                            </select>
                            <select
                                name='group'
                                id='datesType'
                                className='select'
                                value={group}
                                onChange={(e) => {
                                    const selectedType = e.target.value;
                                    setGroup(selectedType);
                                }}
                            >
                                <option value='backpacking'>Backpacking</option>
                                <option value='vehicle'>Vehicle</option>
                            </select>
                        </>
                    )}

                    <select
                        name='trip'
                        id='trip'
                        className='select'
                        value={selectedTrip}
                        onChange={(e) => {
                            const value = e.target.value;
                            setSelectedTrip(value);
                        }}
                    >
                        <option value=''>{serviceType === 'camp' ? 'Select Stay' : 'Select Trip'}</option>
                        {trips.map((trip) => (
                            <option value={trip._id} key={trip._id}>
                                {trip.title}
                            </option>
                        ))}
                    </select>
                    {selectedTrip !== '' && serviceType === 'trip' && (
                        <AddBookingTrip
                            packData={packData}
                            setAddBo={setAddBo}
                            setLoading={setLoading}
                            tripDates={tripDates}
                            refresh={refresh}
                        />
                    )}
                    {selectedTrip !== '' && serviceType === 'act' && (
                        <AddBookingAct actId={packData._id} packData={packData} setAddBo={setAddBo} />
                    )}
                    {selectedTrip !== '' && serviceType === 'gu' && (
                        <AddBookingGu guTourId={packData._id} packData={packData} setAddBo={setAddBo} />
                    )}
                    {selectedTrip !== '' && serviceType === 'camp' && (
                        <StayManualBooking
                            listingId={selectedTrip}
                            onClose={() => setAddBo(false)}
                            onSaved={() => setAddBo(false)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
